// src/pages/Campaigns/SurveyGenie/SurveyDocuments.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Card, CardContent, CardActions,
  IconButton, TextField, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, CircularProgress, Chip, LinearProgress, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, Snackbar, Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  Description as DocIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as BackIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../utils/axiosInstance';
import { hasPermission } from '../../../../utils/permissionUtils';

const SurveyDocuments = () => {
  const { surveyId, chatbotId } = useParams();
  const navigate = useNavigate();

  // State
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [isTraining, setIsTraining] = useState(false);

  // Dialog states
  const [uploadDialog, setUploadDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, document: null });
  const [viewDialog, setViewDialog] = useState({ open: false, document: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, document: null });

  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Permissions
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    const checkPermissions = async () => {
      const modifyPerm = await hasPermission('survey_genie', 'add|edit');
      const deletePerm = await hasPermission('survey_genie', 'delete');
      setCanModify(modifyPerm);
      setCanDelete(deletePerm);
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    if (surveyId) {
      loadDocuments();
    }
  }, [surveyId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/surveys/documents', {
        params: { survey_id: surveyId }
      });
      if (response.data.success) {
        setDocuments(response.data.data);
      }
    } catch (error) {
      showSnackbar('Error loading documents', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['pdf', 'docx'].includes(ext)) {
        showSnackbar('Only .pdf and .docx files are supported', 'error');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        showSnackbar('File size exceeds 10MB limit', 'error');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showSnackbar('Please select a file', 'error');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axiosInstance.post('/api/surveys/documents', formData, {
        params: { survey_id: surveyId },
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        showSnackbar(response.data.message || 'Document uploaded and trained to Knowledge base successfully', 'success');
        setUploadDialog(false);
        setSelectedFile(null);
        loadDocuments();
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.document) return;

    try {
      await axiosInstance.delete('/api/surveys/documents', {
        params: { document_id: deleteDialog.document.id }
      });
      showSnackbar('Document deleted successfully', 'success');
      setDeleteDialog({ open: false, document: null });
      loadDocuments();
    } catch (error) {
      showSnackbar('Error deleting document', 'error');
    }
  };

  const handleTrainSelected = async () => {
    const untrainedIds = selectedDocs.filter(id => {
      const doc = documents.find(d => d.id === id);
      return doc && !doc.is_trained && doc.processing_status === 'completed';
    });

    if (untrainedIds.length === 0) {
      showSnackbar('No untrained documents selected', 'warning');
      return;
    }

    setIsTraining(true);
    try {
      const response = await axiosInstance.post('/api/surveys/documents/train',
        { document_ids: untrainedIds },
        { params: { survey_id: surveyId } }
      );

      if (response.data.success) {
        showSnackbar(response.data.message, 'success');
        setSelectedDocs([]);
        loadDocuments();
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Training failed', 'error');
    } finally {
      setIsTraining(false);
    }
  };

  const handleSelectDoc = (docId) => {
    setSelectedDocs(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDocs.length === filteredDocuments.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(filteredDocuments.map(d => d.id));
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredDocuments = documents.filter(doc =>
    doc.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (doc.summary && doc.summary.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusChip = (status) => {
    const statusColors = {
      pending: 'default',
      processing: 'info',
      completed: 'success',
      error: 'error'
    };
    return <Chip label={status} color={statusColors[status] || 'default'} size="small" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5, mt: { xs: 2, sm: 10 } }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/surveys`)} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              <DocIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Survey Documents
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload documents to provide additional context for this survey
            </Typography>
          </Box>
        </Box>
        {canModify && (
          <Button
            variant="contained"
            size="small"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialog(true)}
          >
            Upload Document
          </Button>
        )}
      </Box>

      {/* Training Action Bar */}
      {selectedDocs.length > 0 && canModify && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Chip label={`${selectedDocs.length} document(s) selected`} color="primary" variant="outlined" />
          <Button
            variant="contained"
            color="primary"
            startIcon={isTraining ? <CircularProgress size={20} color="inherit" /> : <ModelTrainingIcon />}
            onClick={handleTrainSelected}
            disabled={isTraining}
          >
            {isTraining ? 'Training...' : 'Train Selected'}
          </Button>
          <Button variant="text" size="small" onClick={() => setSelectedDocs([])}>
            Clear Selection
          </Button>
        </Paper>
      )}

      {/* Search & Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button variant="outlined" size="small" onClick={handleSelectAll} fullWidth>
              {selectedDocs.length === filteredDocuments.length && filteredDocuments.length > 0
                ? 'Deselect All' : 'Select All'}
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              {filteredDocuments.length} document(s)
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Documents Table */}
      {filteredDocuments.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <DocIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No documents yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload .pdf or .docx files to provide context for your survey
          </Typography>
          {canModify && (
            <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setUploadDialog(true)}>
              Upload Document
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedDocs.length === filteredDocuments.length && filteredDocuments.length > 0}
                    indeterminate={selectedDocs.length > 0 && selectedDocs.length < filteredDocuments.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Filename</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Trained</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDocuments.map((doc) => (
                <TableRow key={doc.id} selected={selectedDocs.includes(doc.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedDocs.includes(doc.id)}
                      onChange={() => handleSelectDoc(doc.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DocIcon sx={{ mr: 1, color: 'primary.main' }} />
                      {doc.filename}
                    </Box>
                  </TableCell>
                  <TableCell>{doc.file_type?.toUpperCase() || 'N/A'}</TableCell>
                  <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                  <TableCell>{getStatusChip(doc.processing_status)}</TableCell>
                  <TableCell>
                    {doc.is_trained ? (
                      <Tooltip title={`Trained on ${new Date(doc.trained_at).toLocaleDateString()}`}>
                        <Chip icon={<CheckCircleIcon />} label="Trained" size="small" color="success" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Chip label="Not Trained" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>{new Date(doc.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Tooltip title="View Content">
                      <IconButton size="small" onClick={() => setViewDialog({ open: true, document: doc })}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    {canModify && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => setEditDialog({ open: true, document: doc })}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, document: doc })}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => !uploading && setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Supported formats: .pdf, .docx (Max 10MB)
          </Typography>
          <Box sx={{ textAlign: 'center', p: 3, border: '2px dashed', borderColor: 'grey.300', borderRadius: 2 }}>
            <input
              type="file"
              accept=".pdf,.docx"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="doc-upload-input"
            />
            <label htmlFor="doc-upload-input">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                Choose File
              </Button>
            </label>
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </Typography>
            )}
          </Box>
          {uploading && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)} disabled={uploading}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!selectedFile || uploading}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, document: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            {viewDialog.document?.filename}
            <IconButton onClick={() => setViewDialog({ open: false, document: null })}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewDialog.document?.summary && (
            <>
              <Typography variant="subtitle2" fontWeight={600}>Summary</Typography>
              <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {viewDialog.document.summary}
              </Typography>
              <Divider sx={{ my: 2 }} />
            </>
          )}
          <Typography variant="subtitle2" fontWeight={600}>Content</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto' }}>
            {viewDialog.document?.page_content || 'No content available'}
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, document: null })} maxWidth="md" fullWidth>
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Summary"
            multiline
            rows={4}
            defaultValue={editDialog.document?.summary || ''}
            sx={{ mt: 2, mb: 2 }}
            onChange={(e) => {
              if (editDialog.document) {
                editDialog.document.summary = e.target.value;
              }
            }}
          />
          <TextField
            fullWidth
            label="Tags (comma separated)"
            defaultValue={editDialog.document?.tags || ''}
            onChange={(e) => {
              if (editDialog.document) {
                editDialog.document.tags = e.target.value;
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, document: null })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await axiosInstance.put('/api/surveys/documents', {
                  summary: editDialog.document.summary,
                  tags: editDialog.document.tags
                }, { params: { document_id: editDialog.document.id } });
                showSnackbar('Document updated', 'success');
                setEditDialog({ open: false, document: null });
                loadDocuments();
              } catch (error) {
                showSnackbar('Update failed', 'error');
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, document: null })}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.document?.filename}"?
            {deleteDialog.document?.is_trained && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This document is trained. Deleting it will also remove it from the knowledge base.
              </Alert>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, document: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SurveyDocuments;